import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import AdminHealth from "@/components/admin/AdminHealth";
import AdminPeopleSection from "@/components/admin/AdminPeopleSection";
import AdminRequests from "@/components/admin/AdminRequests";
import AdminToday from "@/components/admin/AdminToday";
import { UnlockProvider } from "@/components/admin/dialogs";
import { SectionBoundary } from "@/components/admin/ui";

// The admin area: four sections inside the same shell as the rest of the app. Access is
// enforced on the server; this page only draws what the server allows.

const TABS = [["today", "Today"], ["people", "People"], ["health", "Health"], ["requests", "Requests"]] as const;
type Tab = (typeof TABS)[number][0];

export default function Admin() {
  const [params, setParams] = useSearchParams();
  const asked = params.get("tab");
  const tab: Tab = (TABS.find(([k]) => k === asked)?.[0]) || "today";
  const openId = params.get("open");
  const closing = useRef<string | null>(null);
  useEffect(() => { if (!openId) closing.current = null; }, [openId]);
  const go = (next: string, userId?: string) => {
    const q: Record<string, string> = next === "today" ? {} : { tab: next };
    if (userId) q.open = userId;
    // Opening a person adds a step to the browser's history, so the phone's Back button closes
    // the person instead of leaving the admin area. Everything else replaces.
    setParams(q, { replace: !userId });
    if (next !== tab) window.scrollTo({ top: 0 });
  };

  return (
    <UnlockProvider>
    <div data-testid="admin-page">
      <h1 className="font-display text-2xl tracking-tight">Admin</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Counts, dates and statuses only. Nothing anyone wrote is shown here.
      </p>

      <div className="sticky top-[60px] z-10 -mx-4 mt-4 bg-background/90 px-4 py-2 backdrop-blur-xl">
        <div role="tablist" aria-label="Admin sections" data-testid="admin-tabs"
          className="grid grid-cols-4 gap-1 rounded-full bg-muted p-1">
          {TABS.map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key}
              onClick={() => go(key)} data-testid={`admin-tab-${key}`}
              className={`h-11 rounded-full text-sm font-bold transition-colors ${tab === key
                ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4" role="tabpanel">
        <SectionBoundary key={tab}>
          {tab === "today" && <AdminToday onOpen={go} />}
          {tab === "people" && (
            <AdminPeopleSection openId={openId} onOpen={(id) => {
              if (id) return go("people", id);
              // Closing steps back over the entry that opening added, so Back never has a
              // leftover step. Opened straight from a link, there is nothing to step over.
              // Only once per open person: two quick taps must not step back twice.
              if (closing.current === openId) return;
              closing.current = openId;
              if ((window.history.state?.idx ?? 0) > 0) window.history.back();
              else go("people");
            }} />
          )}
          {tab === "health" && <AdminHealth />}
          {tab === "requests" && <AdminRequests />}
        </SectionBoundary>
      </div>
    </div>
    </UnlockProvider>
  );
}
